import { AsyncPipe } from "@angular/common";
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { SelectorComponent } from "@components/selector/selector.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { Observable, Subject } from "rxjs";
import { shareReplay, takeUntil } from "rxjs/operators";
import { Anskaffningsformer } from "~app/common/models/constants";
import { ISelectValue } from "~app/common/models/selectValueModel";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { FeatureService } from "~app/common/services/feature.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ValidationContext } from "~app/common/validation/validationContext";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IAtgard } from "~app/features/planera/model/IAtgard";
import { IAtgardBarighetChanged } from "~app/features/planera/model/IAtgardBarighetChanged";
import { SetEjEudrCommand } from "../commands/SetEjEudrCommand";
import { ArbetsorderMediatorService } from "../services/arbetsorder-mediator.service";
import { ArbetsorderInfoAnteckningarComponent } from "./anteckningar/arbetsorder-info-anteckningar.component";
import { ArbetsorderInfoAnteckningarService } from "./anteckningar/arbetsorder-info-anteckningar.service";
import { IAtgardTab } from "./arbetsorder-information.models";
import { ArbetsorderInformationValidation } from "./arbetsorder-information.validation";
import { ArbetsorderDirektutbyteComponent } from "./direktutbyte/arbetsorder-direktutbyte.component";
import { ArbetsorderDokumentComponent } from "./dokument/arbetsorder-dokument.component";
import { ArbetsorderEudrComponent } from "./EUDR/eudr-extern/arbetsorder-eudr.component";
import { EudrStatusComponent } from "./EUDR/eudr-status/eudr-status.component";
import { ArbetsorderInfoSummaryTerminalimportComponent } from "./sammanfattning/arbetsorder-info-summar-terminalimport/arbetsorder-info-summary-terminalimport.component";
import { ArbetsorderInfoSummaryExternComponent } from "./sammanfattning/arbetsorder-info-summary-extern/arbetsorder-info-summary-extern.component";
import { ArbetsorderInfoSummaryComponent } from "./sammanfattning/arbetsorder-info-summary/arbetsorder-info-summary.component";
import { AtgardInfoSummaryComponent } from "./sammanfattning/atgard-info-summary/atgard-info-summary.component";

@Component({
    selector: "rask-arbetsorder-information",
    templateUrl: "./arbetsorder-information.component.html",
    styleUrls: ["./arbetsorder-information.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        FormsModule,
        TabsComponent,
        TabComponent,
        ArbetsorderInfoSummaryTerminalimportComponent,
        ArbetsorderInfoSummaryExternComponent,
        ArbetsorderInfoSummaryComponent,
        AtgardInfoSummaryComponent,
        ArbetsorderDirektutbyteComponent,
        ArbetsorderInfoAnteckningarComponent,
        ArbetsorderDokumentComponent,
        ArbetsorderEudrComponent,
        EudrStatusComponent,
        RaskCommonModule,
        AsyncPipe
    ]
})
export class ArbetsorderInformationComponent implements OnInit, OnDestroy {

    arbetsorder: IArbetsorderDetail;
    atgarderSelection: IAtgard[];
    atgarderTabs: IAtgardTab[];
    canSave: boolean;
    currentTab: IAtgardTab;
    isEditable: boolean;
    @Input() produktgrupper: ISelectValue[];
    selectedAtgard: IAtgard;
    @Input() updatedAtgardBarighet: IAtgardBarighetChanged[];
    setFormDirty: () => void;
    validationContext: ValidationContext;
    eudrFeatureActive$: Observable<boolean>;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly arbetsorderInfoAnteckningarService = inject(ArbetsorderInfoAnteckningarService);
    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly featureService = inject(FeatureService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly router = inject(Router);

    atgarderSelectionViewFormatter = (atg: IAtgard): string => atg.beteckning;

    getTabName(tab: IAtgardTab): string {
        if (tab.id === "Anteckningar") {
            return `${tab.name}${this.arbetsorderInfoAnteckningarService.getAnteckningarCountPresentationBySelectedItem()}`;
        }
        return tab.name;
    }

    isAtgardSelected(): boolean {
        return this.selectedAtgard?.guid !== GuidUtils.EMPTY;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.eudrFeatureActive$ = this.featureService.getFeatureActive("EudrAnmalan").pipe(
            shareReplay(1)
        );

        this.arbetsorderMediatorService.arbetsorder
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(changeEvent => {
                if (changeEvent?.arbetsorderWrapper?.arbetsorder != null) {
                    this.arbetsorder = changeEvent?.arbetsorderWrapper?.arbetsorder;
                    this.setupArbetsorder(this.arbetsorder);
                }
            });

        this.arbetsorderMediatorService.readonly
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(readonly => {
                this.isEditable = !readonly;
            });

        this.arbetsorderMediatorService.dirty
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(dirty => {
                this.canSave = !dirty;
            });

        this.setFormDirty = () => this.arbetsorderMediatorService.setDirty(true);
    }

    onAtgarderSelectionChanged(): void {
        const dokumentTab = this.atgarderTabs?.find(x => x.id === "Dokument");

        if (this.isAtgardSelected()) {
            this.arbetsorderInfoAnteckningarService.setAnteckningView("Atgard", this.selectedAtgard);

            // Byter till rätt åtgärdsid i URL
            this.router.navigate([
                "planera",
                this.arbetsorder.id
            ], {
                replaceUrl: true,
                queryParams: {
                    atgardId: this.selectedAtgard.id
                }
            });

            if (dokumentTab) {
                dokumentTab.hidden = true;
            }
        } else {
            this.router.navigate([
                "planera",
                this.arbetsorder.id
            ], {
                replaceUrl: true
            });
            this.arbetsorderInfoAnteckningarService.setAnteckningView("Arbetsorder", this.arbetsorder);
            if (dokumentTab) {
                dokumentTab.hidden = false;
            }
        }

        if (dokumentTab?.hidden && this.currentTab === dokumentTab) {
            this.currentTab = this.atgarderTabs.find(x => x.id === "Sammanfattning");
        }
    }

    setTab(tab: IAtgardTab): void {
        this.currentTab = tab;
    }

    visibleAtgarderTabs = () => this.atgarderTabs.filter(p => !p.hidden);

    setEjEudr = (ejEudr: boolean) => {
        const cmd = new SetEjEudrCommand((instance) => {
            instance.arbetsorderId = this.arbetsorder.id;
            instance.ejEudr = ejEudr;
        });
        this.arbetsorder.commandManager.addOrUpdate(cmd, (c) => c.$type === SetEjEudrCommand.$type && c.arbetsorderId === this.arbetsorder.id);
    };

    protected isImport(): boolean {
        return this.arbetsorder.anskaffningsform === Anskaffningsformer.Import;
    }

    private setupArbetsorder(arbetsorder: IArbetsorderDetail): void {
        this.setupAtgarder();
        this.setupValidation();
        this.arbetsorderInfoAnteckningarService.initSettings(arbetsorder);
        const atgardId = +this.activatedRoute.snapshot.queryParamMap.get("atgardId");
        if (atgardId > 0) {
            const foundAtgard = this.atgarderSelection.find(atg => atg.id === atgardId);
            if (foundAtgard) {
                this.selectedAtgard = foundAtgard;
            }
        }
        this.setupTabs();
        this.onAtgarderSelectionChanged();
    }

    private setupAtgarder(): void {
        const summaryAtgard: any = {
            guid: GuidUtils.EMPTY,
            beteckning: "Alla ingående åtgärder"
        };

        let atgarder = [];
        atgarder.push(summaryAtgard);
        atgarder = atgarder.concat(this.arbetsorder.atgarder);
        this.atgarderSelection = atgarder;

        // Om det är en ngOnChanges så kan det vara samma AO som kommer och då vill vi behålla åtgärdsvalet
        if (this.selectedAtgard && this.atgarderSelection.find(atg => atg.id === this.selectedAtgard.id)) {
            this.selectedAtgard = this.atgarderSelection.find(atg => atg.id === this.selectedAtgard.id);
        } else {
            this.selectedAtgard = summaryAtgard;
        }
    }

    private setupTabs(): void {
        this.atgarderTabs = [];
        this.atgarderTabs.push({
            name: "Sammanfattning",
            id:"Sammanfattning",
            hidden: false
        });
        this.atgarderTabs.push({
            name: "Direktutbyte",
            id:"Direktutbyte",
            hidden: this.arbetsorder.isTerminalImport || this.arbetsorder.isExternArbetsorder ? true : false
        });
        this.atgarderTabs.push({
            name: "Anteckningar",
            id:"Anteckningar",
            hidden: this.arbetsorder.isTerminalImport || this.arbetsorder.isExternArbetsorder ? true : false
        });
        this.atgarderTabs.push({
            name: "Dokument",
            id:"Dokument",
            hidden: this.arbetsorder.isTerminalImport || this.arbetsorder.isExternArbetsorder ? true : false
        });
        this.atgarderTabs.push({
            name: "EUDR",
            id:"EUDR",
            hidden:  false
        });
    }

    private setupValidation(): void {
        this.validationContext = ArbetsorderInformationValidation.init(this.arbetsorder);
        this.arbetsorderMediatorService.addValidationContext(this.validationContext);
        this.arbetsorderMediatorService.registerValidationFactory(ArbetsorderInformationValidation.init);
    }
}
